/**
 * Parent-to-iframe message handler.
 * Verbatim slice of the iframe bridge script (optimization Phase 10c) —
 * returned text is embedded inside the bridge IIFE; it is NOT module code.
 * The slice is interpolation-free: keep it that way (head/tail interpolations
 * live in iframe-bridge.js).
 */

export function messageHandlerLayer() {
  return String.raw`  // --- AI chat context targets ---
  // Places the user tagged in the agent composer. Held here rather than
  // recomputed from attributes because surgical DOM swaps discard the
  // attributes but not the intent; every swap path reapplies from this list.
  var _aiContextTargets = [];

  function _clearAiContextMarkers(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var markers = scope.querySelectorAll('[data-pb-ai-context-marker]');
    for (var m = 0; m < markers.length; m++) {
      if (markers[m].parentNode) markers[m].parentNode.removeChild(markers[m]);
    }
  }

  function _addAiContextMarker(anchor, wrapper, label) {
    if (!anchor || !wrapper) return;
    var marker = document.createElement('span');
    marker.className = 'pb-pill-marker pb-ai-context-marker';
    marker.setAttribute('data-pb-ai-context-marker', '1');
    marker.setAttribute('data-pb-label', label || wrapper.getAttribute('data-pb-label') || 'Selection');
    marker.setAttribute('aria-hidden', 'true');
    marker.style.setProperty('position', 'absolute', 'important');
    if (anchor === wrapper) wrapper.appendChild(marker);
    else if (anchor.parentNode) anchor.parentNode.insertBefore(marker, anchor.nextSibling);
    _positionPillMarker(marker, anchor);
  }

  function _applyAiContext() {
    _clearAiContextMarkers();
    var current = document.querySelectorAll('[data-pb-ai-context]');
    for (var i = 0; i < current.length; i++) {
      current[i].removeAttribute('data-pb-ai-context');
    }
    for (var j = 0; j < _aiContextTargets.length; j++) {
      var target = _aiContextTargets[j];
      if (!target || !target.instanceId) continue;
      var wrapper = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(target.instanceId) + '"]');
      if (!wrapper) continue;
      if (!target.eid) {
        wrapper.setAttribute('data-pb-ai-context', 'section');
        _addAiContextMarker(wrapper, wrapper, target.label);
        continue;
      }
      // Always scoped to the wrapper: eids are unique per section only.
      var el = wrapper.querySelector('[data-pb-eid="' + _escapeSelectorValue(target.eid) + '"]');
      if (el) {
        el.setAttribute('data-pb-ai-context', 'element');
        _addAiContextMarker(el, wrapper, target.label);
      }
    }
  }

  function _setAiPickMode(active) {
    if (!document.body) return;
    if (active) document.body.classList.add('pb-ai-pick-mode');
    else document.body.classList.remove('pb-ai-pick-mode');
  }

  // Comment mode's cursor half. The parent can only style the iframe ELEMENT;
  // the crosshair inside this document has to be driven from in here.
  // LG-4. 72 of 330 corpus headers restyle their logo on scroll, and until now
  // that second design was only reachable by scrolling past it — you could not
  // hold it, so you sized a logo against the state you could see and met the
  // other one on the shipped site.
  //
  // The pin is a body class plus a flag the section-JS facade reads: the parser
  // injects a scroll listener into every template nav that REMOVES the class
  // below 100px, so setting the class alone would be undone by the next scroll
  // event. The facade suppresses removals while the flag is set.
  //
  // The section roots are toggled here as well as the body, because
  // the facade's mirror lives inside each section's own closure and
  // only fires for writes that go THROUGH the facade proxy — a parent-driven
  // pin does not.
  function _setScrollState(pinned) {
    if (!document.body) return;
    window.__PB_SCROLL_PINNED__ = !!pinned;
    document.body.classList.toggle('scroll', !!pinned);
    var roots = document.querySelectorAll('[data-pb-section]');
    for (var si = 0; si < roots.length; si++) {
      roots[si].classList.toggle('scroll', !!pinned);
    }
  }
  function _setCommentMode(active) {
    if (!document.body) return;
    if (active) document.body.classList.add('pb-comment-mode');
    else document.body.classList.remove('pb-comment-mode');
  }

  function _imageReplaceMotionAllowed() {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return false;
      }
    } catch (_) {}
    return true;
  }

  function _scrubMotionGhost(root) {
    if (!root || !root.querySelectorAll) return;
    root.removeAttribute('id');
    root.removeAttribute('data-pb-editable');
    root.removeAttribute('data-pb-eid');
    root.removeAttribute('data-pb-selected-target');
    root.removeAttribute('data-pb-selected-label');
    var ids = root.querySelectorAll('[id]');
    for (var i = 0; i < ids.length; i++) ids[i].removeAttribute('id');
    var editables = root.querySelectorAll('[data-pb-editable], [data-pb-eid], [data-pb-selected-target], [data-pb-selected-label]');
    for (var j = 0; j < editables.length; j++) {
      editables[j].removeAttribute('data-pb-editable');
      editables[j].removeAttribute('data-pb-eid');
      editables[j].removeAttribute('data-pb-selected-target');
      editables[j].removeAttribute('data-pb-selected-label');
    }
  }

  function _prepareImageReplaceCrossfade(target) {
    if (!target || !target.parentNode || !_imageReplaceMotionAllowed()) return null;
    if (!document.body) return null;
    if (!target.getBoundingClientRect) return null;
    var rect = target.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;
    var ghost = target.cloneNode(true);
    _scrubMotionGhost(ghost);
    ghost.setAttribute('aria-hidden', 'true');
    ghost.setAttribute('data-pb-image-replace-ghost', '1');
    var ghostStyle = ghost.style;
    ghostStyle.position = 'fixed';
    ghostStyle.left = rect.left + 'px';
    ghostStyle.top = rect.top + 'px';
    ghostStyle.width = rect.width + 'px';
    ghostStyle.height = rect.height + 'px';
    ghostStyle.margin = '0';
    ghostStyle.pointerEvents = 'none';
    ghostStyle.zIndex = '2147483000';
    ghostStyle.opacity = '1';
    ghostStyle.transform = 'none';
    ghostStyle.transformOrigin = 'center center';
    var tag = target.tagName ? target.tagName.toLowerCase() : '';
    if (tag === 'img') {
      var currentSrc = target.currentSrc || target.getAttribute('src') || '';
      if (currentSrc) ghost.setAttribute('src', currentSrc);
      try {
        var targetStyle = getComputedStyle(target);
        ghostStyle.objectFit = targetStyle.objectFit;
        ghostStyle.objectPosition = targetStyle.objectPosition;
        ghostStyle.borderRadius = targetStyle.borderRadius;
      } catch (_) {}
    }
    document.body.appendChild(ghost);
    return ghost;
  }

  function _animateReplaceOpacity(el, fromOpacity, toOpacity, done) {
    if (!el) {
      if (done) done();
      return;
    }
    if (typeof el.animate === 'function') {
      var anim = el.animate(
        [{ opacity: fromOpacity }, { opacity: toOpacity }],
        { duration: 200, easing: 'ease-out', fill: 'none' }
      );
      if (anim && anim.finished && typeof anim.finished.then === 'function') {
        anim.finished.then(function() {
          if (done) done();
        }).catch(function() {
          if (done) done();
        });
        return;
      }
    }
    var previousTransition = el.style.transition;
    var previousOpacity = el.style.opacity;
    el.style.opacity = String(fromOpacity);
    el.style.transition = 'opacity 200ms ease-out';
    var raf = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : function(fn) { return setTimeout(fn, 16); };
    raf(function() {
      el.style.opacity = String(toOpacity);
    });
    setTimeout(function() {
      el.style.transition = previousTransition;
      el.style.opacity = previousOpacity;
      if (done) done();
    }, 220);
  }

  function _finishImageReplaceCrossfade(target, ghost) {
    if (!target || !_imageReplaceMotionAllowed()) {
      if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
      return;
    }
    var targetOpacity = '1';
    try {
      targetOpacity = getComputedStyle(target).opacity || '1';
    } catch (_) {}
    _animateReplaceOpacity(target, '0', targetOpacity, null);
    if (ghost) {
      _animateReplaceOpacity(ghost, '1', '0', function() {
        if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      });
    }
  }

  // --- Message handler (parent → iframe) ---
  window.addEventListener('message', function(e) {
    var msg = e.data;
    if (!msg || msg.source !== 'pb-parent' || msg.nonce !== __PB_NONCE__) return;

      switch (msg.type) {
      case 'enable-editing': {
        if (_previewMode) break;
        var target = findEditableByPath(msg.data);
        if (!target) break;
        if (activeEditable && activeEditable !== target) finishEditing();
        var requestedSessionId = Number(msg.data && msg.data.editSessionId);
        if (Number.isFinite(requestedSessionId) && requestedSessionId > 0) {
          _activeEditSessionId = requestedSessionId;
          if (requestedSessionId >= _editSessionCounter) _editSessionCounter = requestedSessionId + 1;
        } else {
          _activeEditSessionId = _nextEditSessionId();
        }
        _plainTextOnly = !!(msg.data && msg.data.plainTextOnly);
        activeEditable = target;
        activeEditableInitialHTML = target.innerHTML;
        activeEditableInitialTagName = target.tagName;
        _lastPastePayload = null;
        _lastPasteRangeSnapshot = null;
        _lastPasteInsertedNodes = null;
        _lastPasteReplacedNodes = null;
        _lastUndonePastePayload = null;
        _editOperationUndoStack = [];
        _editOperationRedoStack = [];
        _isComposingText = false;
        _escapeCancelArmedUntil = 0;
        _intentionallyRemovedProtectedPaths = [];
        _activeEditableDirty = false;
        _sentSelectionState = 'none';
        target.classList.remove('pb-edit-empty');
        target.setAttribute('contenteditable', _plainTextOnly ? 'plaintext-only' : 'true');
        target.classList.add('pb-editing');
        _suspendDragForTextEditing(target);
        _updateEditingPill(target);
        applyEditingAria(target);
        _focusActiveEditable();
        if (!restoreSelectionSnapshot(target, msg.data && msg.data.caret ? msg.data.caret : null)) {
          lastRange = null;
        }
        _sendBoundInfoFromActiveEditable();
        _scheduleSelectionSync();
        break;
      }
      case 'disable-editing': {
        finishEditing();
        break;
      }
      case 'text-history-shortcut': {
        if (!activeEditable) break;
        var historyCommand = msg.data && msg.data.command === 'redo' ? 'redo' : 'undo';
        _applyNativeHistoryShortcut({
          altKey: false,
          isComposing: false,
          shiftKey: false,
          preventDefault: function() {},
          stopImmediatePropagation: function() {}
        }, historyCommand === 'redo' ? 'y' : 'z');
        break;
      }
      case 'set-selected-section': {
        var selectSectionData = msg.data || {};
        _setSelectedSection(selectSectionData.instanceId || null, { flash: !!selectSectionData.flash });
        break;
      }
      case 'clear-selected-section': {
        _clearSelectedSection((msg.data && msg.data.reason) || 'parent-clear');
        break;
      }
      case 'set-multi-selected-sections': {
        // 16A1 — co-selected wrappers. An empty list clears the multi attrs;
        // the scalar (lead) is driven separately by set-selected-section.
        var multiData = msg.data || {};
        _setMultiSelectedSections(multiData.instanceIds || [], multiData.leadId || null);
        break;
      }
      case 'set-multi-selected-elements': {
        // 16C1 — co-selected repeat-root siblings. The LEAD keeps its existing
        // data-pb-selected-target treatment; an empty list clears the members.
        var multiElData = msg.data || {};
        _setMultiSelectedElements(
          multiElData.eids || [],
          multiElData.leadEid || null,
          multiElData.itemSelector || null
        );
        break;
      }
      case 'set-hovered-section': {
        _setHoveredSection((msg.data && msg.data.instanceId) || null, { elementHovered: false });
        break;
      }
      case 'set-collab-active': {
        // Collab Phase 10: enable/disable the pointer reporter (solo → off).
        if (typeof window.__pbSetCollabReportActive === 'function') {
          window.__pbSetCollabReportActive(!!(msg.data && msg.data.active));
        }
        break;
      }
      case 'set-collab-presence': {
        // Collab Phase 10: light peer-presence badge on section wrappers
        // (distinct from the lock nameplate). Full replacement each time.
        var prevPresent = document.querySelectorAll('[data-pb-collab-presence]');
        for (var pp = 0; pp < prevPresent.length; pp++) {
          prevPresent[pp].removeAttribute('data-pb-collab-presence');
          prevPresent[pp].style.removeProperty('--pb-collab-presence-color');
        }
        var presenceRows = (msg.data && msg.data.rows) || [];
        for (var pr = 0; pr < presenceRows.length; pr++) {
          var prow = presenceRows[pr] || {};
          if (!prow.instanceId) continue;
          var presWrapper = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(prow.instanceId) + '"]');
          if (!presWrapper) continue;
          presWrapper.setAttribute('data-pb-collab-presence', '1');
          if (prow.color) presWrapper.style.setProperty('--pb-collab-presence-color', prow.color);
        }
        break;
      }
      case 'set-collab-locks': {
        // Collab Phase 9: stamp peer-lock treatment on section wrappers. Full
        // replacement each time; re-sent by the parent on bridge-ready.
        var prevLocked = document.querySelectorAll('[data-pb-collab-locked]');
        for (var pl = 0; pl < prevLocked.length; pl++) {
          prevLocked[pl].removeAttribute('data-pb-collab-locked');
          prevLocked[pl].removeAttribute('data-pb-collab-name');
        }
        var lockRows = (msg.data && msg.data.locks) || [];
        for (var lr = 0; lr < lockRows.length; lr++) {
          var row = lockRows[lr] || {};
          if (!row.instanceId) continue;
          var lockWrapper = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(row.instanceId) + '"]');
          if (!lockWrapper) continue;
          lockWrapper.setAttribute('data-pb-collab-locked', '1');
          lockWrapper.setAttribute('data-pb-collab-name', row.name || 'Editing');
        }
        break;
      }
      case 'set-changed-sections': {
        // Agent Review AR-6: runtime-only changed-section treatment. The
        // parent always sends the complete set, so replay and duplicate sends
        // are idempotent and stale wrappers clear without diff bookkeeping.
        var prevChanged = document.querySelectorAll('[data-pb-section-changed]');
        for (var pc = 0; pc < prevChanged.length; pc++) {
          prevChanged[pc].removeAttribute('data-pb-section-changed');
          prevChanged[pc].removeAttribute('data-pb-change-label');
        }
        var changedRows = (msg.data && msg.data.rows) || [];
        for (var cr = 0; cr < changedRows.length; cr++) {
          var changedRow = changedRows[cr] || {};
          if (!changedRow.instanceId) continue;
          var changedWrapper = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(changedRow.instanceId) + '"]');
          if (!changedWrapper) continue;
          changedWrapper.setAttribute('data-pb-section-changed', '1');
          changedWrapper.setAttribute('data-pb-change-label', changedRow.label || 'Agent change');
        }
        break;
      }
      case 'cancel-reorder': {
        if (_reorderState && _reorderState.activated) _cancelReorder('parent-watchdog');
        break;
      }
      case 'flush-pending-edit': {
        var requestedFlushSessionId = msg.data && typeof msg.data.editSessionId === 'number'
          ? msg.data.editSessionId
          : null;
        clearTimeout(debounceTimer);
        debounceTimer = null;
        if (activeEditable) {
          _sendContentChangedNow(_activeEditSessionId);
        }
        send('flush-pending-edit-ack', {
          editSessionId: requestedFlushSessionId
        });
        break;
      }
      case 'update-image': {
        var imgTarget = findEditableByPath(msg.data);
        if (imgTarget && imgTarget.tagName === 'IMG') {
          var scope = String((msg.data && msg.data.pictureReplaceScope) || 'all').toLowerCase();
          if (scope !== 'mobile' && scope !== 'desktop') scope = 'all';
          var imageReplaceGhost = _prepareImageReplaceCrossfade(imgTarget);
          var applyImgSrc = function() {
            imgTarget.src = msg.data.src;
            // Clear responsive sources so the new src wins in the live preview too
            // (mirrors updateImageInHTML on the persisted HTML).
            imgTarget.removeAttribute('srcset');
            imgTarget.removeAttribute('sizes');
            imgTarget.removeAttribute('data-src');
            imgTarget.removeAttribute('data-srcset');
            if (typeof msg.data.alt === 'string' && !imgTarget.hasAttribute('alt')) {
              imgTarget.setAttribute('alt', msg.data.alt);
            }
            if (msg.data.assetId) {
              imgTarget.setAttribute('data-pb-asset', msg.data.assetId);
            } else {
              imgTarget.removeAttribute('data-pb-asset');
            }
          };
          if (scope !== 'mobile') applyImgSrc();
          var picture = imgTarget.closest('picture');
          if (picture) {
            var picSources = picture.querySelectorAll('source');
            for (var si = 0; si < picSources.length; si++) {
              var source = picSources[si];
              var media = source.getAttribute('media') || '';
              var isMobileSource = /\bmax-width\b/i.test(media);
              var isDesktopSource = !media || /\bmin-width\b/i.test(media) || !isMobileSource;
              var shouldUpdate = scope === 'all'
                || (scope === 'mobile' && isMobileSource)
                || (scope === 'desktop' && isDesktopSource);
              if (!shouldUpdate) continue;
              source.setAttribute('srcset', msg.data.src);
              source.removeAttribute('sizes');
              source.removeAttribute('data-srcset');
              if (media) {
                source.removeAttribute('type');
              } else if (msg.data.mimeType) {
                source.setAttribute('type', msg.data.mimeType);
              } else {
                source.removeAttribute('type');
              }
            }
          } else if (scope === 'mobile') {
            applyImgSrc();
          }
          _finishImageReplaceCrossfade(imgTarget, imageReplaceGhost);
        }
        markElementRectDirty();
        markHoverTagDirty();
        break;
      }
      case 'update-logo-vars': {
        var logoTarget = findEditableByPath(msg.data);
        if (logoTarget && logoTarget.getAttribute('data-pb-editable') === 'logo') {
          var wrapperAttrs = msg.data && msg.data.wrapperAttrs ? msg.data.wrapperAttrs : {};
          var allowedLogoAttrs = ['data-logo-hover', 'aria-label', 'href'];
          for (var lai = 0; lai < allowedLogoAttrs.length; lai++) {
            var attrName = allowedLogoAttrs[lai];
            if (!(attrName in wrapperAttrs)) continue;
            var attrValue = wrapperAttrs[attrName];
            if (attrValue == null || attrValue === '') logoTarget.removeAttribute(attrName);
            else logoTarget.setAttribute(attrName, String(attrValue));
          }

          var wrapperStyle = msg.data && msg.data.wrapperStyle ? msg.data.wrapperStyle : {};
          var allowedLogoWrapperStyles = ['width', 'height', 'max-width'];
          for (var lwsi = 0; lwsi < allowedLogoWrapperStyles.length; lwsi++) {
            var wrapperStyleName = allowedLogoWrapperStyles[lwsi];
            if (!(wrapperStyleName in wrapperStyle)) continue;
            var wrapperStyleValue = wrapperStyle[wrapperStyleName];
            if (wrapperStyleValue == null || wrapperStyleValue === '') {
              logoTarget.style.removeProperty(wrapperStyleName);
            } else {
              logoTarget.style.setProperty(wrapperStyleName, String(wrapperStyleValue), 'important');
            }
          }

          var slot = logoTarget.querySelector('.logo-slot');
          var slotAttrs = msg.data && msg.data.slotAttrs ? msg.data.slotAttrs : {};
          var allowedLogoSlotAttrs = ['data-logo-engaged', 'data-logo-sized'];
          var slotStyle = msg.data && msg.data.slotStyle ? msg.data.slotStyle : {};
          // --pb-logo-aspect-ratio is deliberately NOT breakpoint-scoped below:
          // one asset has one set of proportions. It is on the wire so a replace
          // re-shapes the slot in the live preview rather than waiting for the
          // next full render.
          var allowedLogoVars = ['--pb-logo-tx', '--pb-logo-ty', '--pb-logo-tx-pct', '--pb-logo-ty-pct', '--pb-logo-scale', '--pb-logo-slot-width', '--pb-logo-slot-height', '--pb-logo-aspect-ratio'];
          var logoBreakpoints = ['mobile', 'tablet', 'desktop'];
          for (var lb = 0; lb < logoBreakpoints.length; lb++) {
            allowedLogoVars.push('--pb-logo-tx-' + logoBreakpoints[lb]);
            allowedLogoVars.push('--pb-logo-ty-' + logoBreakpoints[lb]);
            allowedLogoVars.push('--pb-logo-tx-pct-' + logoBreakpoints[lb]);
            allowedLogoVars.push('--pb-logo-ty-pct-' + logoBreakpoints[lb]);
            allowedLogoVars.push('--pb-logo-scale-' + logoBreakpoints[lb]);
            allowedLogoVars.push('--pb-logo-slot-width-' + logoBreakpoints[lb]);
            allowedLogoVars.push('--pb-logo-slot-height-' + logoBreakpoints[lb]);
          }
          if (slot) {
            for (var lsai = 0; lsai < allowedLogoSlotAttrs.length; lsai++) {
              var slotAttrName = allowedLogoSlotAttrs[lsai];
              if (!(slotAttrName in slotAttrs)) continue;
              var slotAttrValue = slotAttrs[slotAttrName];
              if (slotAttrValue == null || slotAttrValue === '' || slotAttrValue === false) slot.removeAttribute(slotAttrName);
              else slot.setAttribute(slotAttrName, String(slotAttrValue));
            }
            for (var lvi = 0; lvi < allowedLogoVars.length; lvi++) {
              var varName = allowedLogoVars[lvi];
              if (!(varName in slotStyle)) continue;
              var varValue = slotStyle[varName];
              if (varValue == null || varValue === '') slot.style.removeProperty(varName);
              else slot.style.setProperty(varName, String(varValue));
            }
          }
        }
        markElementRectDirty();
        markHoverTagDirty();
        break;
      }
      case 'update-logo-image': {
        var logoImageWrapper = findEditableByPath(msg.data);
        if (logoImageWrapper && logoImageWrapper.getAttribute('data-pb-editable') === 'logo') {
          var logoImages = ensureLogoImageTargets(logoImageWrapper, msg.data && msg.data.variant);
          for (var li = 0; li < logoImages.length; li++) {
            applyLogoImageAttrs(logoImages[li], msg.data);
          }
          var replacingSlot = logoImageWrapper.querySelector('.logo-slot');
          if (replacingSlot) {
            replacingSlot.removeAttribute('data-logo-replacing');
            // Force a fresh animation when the same slot is replaced twice.
            void replacingSlot.offsetWidth;
            replacingSlot.setAttribute('data-logo-replacing', 'true');
            setTimeout(function() {
              replacingSlot.removeAttribute('data-logo-replacing');
            }, 220);
          }
        }
        markElementRectDirty();
        markHoverTagDirty();
        break;
      }
      case 'update-image-vars': {
        var imageTarget = findEditableByPath(msg.data);
        var imageTagName = imageTarget && imageTarget.tagName ? imageTarget.tagName.toLowerCase() : '';
        var imageEditable = imageTarget && imageTarget.getAttribute('data-pb-editable');
        if (imageTarget && imageTagName === 'img' && imageEditable === 'image') {
          var imgVars = msg.data && msg.data.vars ? msg.data.vars : {};
          var imgAttrs = msg.data && msg.data.attrs ? msg.data.attrs : {};
          var allowedImgVars = ['--pb-image-tx-pct', '--pb-image-ty-pct', '--pb-image-scale'];
          var allowedImgHoverValues = ['none', 'scale', 'lift', 'rotate', 'shimmer', 'color-shift'];
          var allowedImgEffectValues = ['none', 'dim', 'darken', 'grayscale', 'warm', 'cool'];

          for (var ivi = 0; ivi < allowedImgVars.length; ivi++) {
            var imgVarName = allowedImgVars[ivi];
            if (!(imgVarName in imgVars)) continue;
            var imgVarValue = imgVars[imgVarName];
            if (imgVarValue == null || imgVarValue === '') {
              imageTarget.style.removeProperty(imgVarName);
            } else {
              imageTarget.style.setProperty(imgVarName, String(imgVarValue));
            }
          }

          if ('data-image-edit' in imgAttrs) {
            var imgEditVal = imgAttrs['data-image-edit'];
            if (imgEditVal == null || imgEditVal === false) {
              imageTarget.removeAttribute('data-image-edit');
            } else {
              imageTarget.setAttribute('data-image-edit', '');
            }
          }
          if ('data-image-hover' in imgAttrs) {
            var imgHoverVal = imgAttrs['data-image-hover'];
            if (imgHoverVal == null || imgHoverVal === '' || imgHoverVal === 'none') {
              imageTarget.removeAttribute('data-image-hover');
            } else {
              var imgHoverStr = String(imgHoverVal);
              var imgHoverOk = false;
              for (var ihv = 0; ihv < allowedImgHoverValues.length; ihv++) {
                if (allowedImgHoverValues[ihv] === imgHoverStr) { imgHoverOk = true; break; }
              }
              if (imgHoverOk) imageTarget.setAttribute('data-image-hover', imgHoverStr);
            }
          }
          if ('data-image-effect' in imgAttrs) {
            var imgEffectVal = imgAttrs['data-image-effect'];
            if (imgEffectVal == null || imgEffectVal === '' || imgEffectVal === 'none') {
              imageTarget.removeAttribute('data-image-effect');
            } else {
              var imgEffectStr = String(imgEffectVal);
              var imgEffectOk = false;
              for (var ief = 0; ief < allowedImgEffectValues.length; ief++) {
                if (allowedImgEffectValues[ief] === imgEffectStr) { imgEffectOk = true; break; }
              }
              if (imgEffectOk) imageTarget.setAttribute('data-image-effect', imgEffectStr);
            }
          }
          if ('data-image-dragging' in imgAttrs) {
            var imgDragVal = imgAttrs['data-image-dragging'];
            if (imgDragVal == null || imgDragVal === false) {
              imageTarget.removeAttribute('data-image-dragging');
            } else {
              imageTarget.setAttribute('data-image-dragging', '');
            }
          }
        }
        markElementRectDirty();
        markHoverTagDirty();
        break;
      }
      case 'update-svg': {
        var svgTarget = findEditableByPath(msg.data);
        if (svgTarget && svgTarget.parentNode) {
          // Use replaceChild instead of outerHTML — outerHTML is unreliable on SVGElement
          var svgTemp = document.createElement('div');
          svgTemp.innerHTML = msg.data.svgMarkup;
          var newSvgEl = svgTemp.firstElementChild;
          if (newSvgEl) {
            var svgWasSelected = _selectedEditable === svgTarget;
            if (svgWasSelected) _clearSelectedState(svgTarget);
            var oldSvgEditable = svgTarget.getAttribute('data-pb-editable');
            var oldSvgEid = svgTarget.getAttribute('data-pb-eid');
            if (oldSvgEditable && !newSvgEl.getAttribute('data-pb-editable')) {
              newSvgEl.setAttribute('data-pb-editable', oldSvgEditable);
            }
            if (oldSvgEid && !newSvgEl.getAttribute('data-pb-eid')) {
              newSvgEl.setAttribute('data-pb-eid', oldSvgEid);
            }
            svgTarget.parentNode.replaceChild(newSvgEl, svgTarget);
            _syncEditableDisplayAttributes(newSvgEl.parentElement || document);
            initScrollAnimations();
            if (activeEditable === svgTarget) {
              activeEditable = newSvgEl;
              observeSelectedEditable(activeEditable);
            }
            if (svgWasSelected) _setSelectedEditable(newSvgEl);
          }
        }
        markPositionsDirty();
        markElementRectDirty();
        markHoverTagDirty();
        break;
      }
      case 'bulk-replace-hrefs': {
        var fromHref = msg.data && typeof msg.data.fromHref === 'string' ? msg.data.fromHref : '';
        var toHref = msg.data && typeof msg.data.toHref === 'string' ? msg.data.toHref : '';
        if (!fromHref) break;

        var hrefAnchors = document.querySelectorAll('a[href]');
        for (var hi = 0; hi < hrefAnchors.length; hi++) {
          var hrefAnchor = hrefAnchors[hi];
          if ((hrefAnchor.getAttribute('href') || '') === fromHref) {
            hrefAnchor.setAttribute('href', toHref);
          }
        }
        markElementRectDirty();
        markHoverTagDirty();
        break;
      }
      case 'swap-image-to-svg': {
        var swapImgTarget = findEditableByPath(msg.data);
        if (!swapImgTarget || swapImgTarget.tagName !== 'IMG') break;

        var swapSvgDoc = new DOMParser().parseFromString(msg.data && msg.data.svgMarkup ? msg.data.svgMarkup : '', 'image/svg+xml');
        var swapNewSvg = swapSvgDoc && swapSvgDoc.documentElement;
        if (!swapNewSvg || String(swapNewSvg.tagName || '').toLowerCase() !== 'svg') break;

        var swapPicture = swapImgTarget.closest ? swapImgTarget.closest('picture') : null;
        var swapReplaceTarget = swapPicture || swapImgTarget;
        if (!swapReplaceTarget.parentNode) break;

        var keepAttrs = ['class', 'style'];
        var swapAttrSources = swapReplaceTarget === swapImgTarget
          ? [swapReplaceTarget]
          : [swapReplaceTarget, swapImgTarget];
        for (var si = 0; si < swapAttrSources.length; si++) {
          var swapAttrSource = swapAttrSources[si];
          for (var ka = 0; ka < keepAttrs.length; ka++) {
            var keepName = keepAttrs[ka];
            var keepVal = swapAttrSource.getAttribute(keepName);
            if (keepVal && !swapNewSvg.getAttribute(keepName)) {
              swapNewSvg.setAttribute(keepName, keepVal);
            }
          }
          for (var ai = 0; ai < swapAttrSource.attributes.length; ai++) {
            var swapAttr = swapAttrSource.attributes[ai];
            if (!swapAttr || !swapAttr.name) continue;
            if (swapAttr.name.indexOf('data-') !== 0) continue;
            if (swapAttr.name === 'data-pb-editable') continue;
            if (!swapNewSvg.hasAttribute(swapAttr.name)) {
              swapNewSvg.setAttribute(swapAttr.name, swapAttr.value);
            }
          }
        }

        // Carry the source <img>'s width/height onto the swapped-in SVG so the
        // box is unchanged (mirrors swapEditableInHTML on the persisted HTML).
        var swapDims = ['width', 'height'];
        for (var sdi = 0; sdi < swapDims.length; sdi++) {
          var swapDimName = swapDims[sdi];
          var swapDimVal = swapImgTarget.getAttribute(swapDimName);
          if (swapDimVal && !swapNewSvg.getAttribute(swapDimName)) {
            swapNewSvg.setAttribute(swapDimName, swapDimVal);
          }
        }

        var swapEid = swapImgTarget.getAttribute('data-pb-eid');
        if (swapEid && !swapNewSvg.getAttribute('data-pb-eid')) {
          swapNewSvg.setAttribute('data-pb-eid', swapEid);
        }
        if (!swapNewSvg.getAttribute('data-pb-editable')) {
          swapNewSvg.setAttribute('data-pb-editable', 'svg');
        }

        var swapImported = document.importNode ? document.importNode(swapNewSvg, true) : swapNewSvg.cloneNode(true);

        if (activeEditable && (activeEditable === swapImgTarget || swapReplaceTarget.contains(activeEditable))) {
          finishEditing();
        }

        var swapWasSelected = !!(_selectedEditable && (_selectedEditable === swapImgTarget || swapReplaceTarget.contains(_selectedEditable)));
        if (swapWasSelected) _clearSelectedState(_selectedEditable);

        swapReplaceTarget.parentNode.replaceChild(swapImported, swapReplaceTarget);
        syncAllTextEditablePlaceholders();
        _syncEditableDisplayAttributes(swapImported.parentElement || document);
        initScrollAnimations();

        if (swapWasSelected) _setSelectedEditable(swapImported);

        markPositionsDirty();
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('editable-swap');
        _injectHeaderZoneMarkers();
        break;
      }
      case 'replace-editable-html': {
        if (!msg.data || !msg.data.instanceId || !msg.data.eid) break;
        var replaceWrapper = document.querySelector('[data-pb-instance="' + msg.data.instanceId + '"]');
        if (!replaceWrapper) break;
        var replaceTarget = replaceWrapper.querySelector('[data-pb-eid="' + msg.data.eid + '"]');
        if (!replaceTarget || !replaceTarget.parentNode) break;

        if (activeEditable && (activeEditable === replaceTarget || replaceTarget.contains(activeEditable))) {
          finishEditing();
        }

        var replaceTemp = document.createElement('div');
        replaceTemp.innerHTML = msg.data.html || '';
        var replaceNewEl = replaceTemp.firstElementChild;
        if (!replaceNewEl) break;
        var replaceImageGhost = msg.data.animate === 'image-replace'
          ? _prepareImageReplaceCrossfade(replaceTarget)
          : null;

        var replaceWasSelected = !!(_selectedEditable && (_selectedEditable === replaceTarget || replaceTarget.contains(_selectedEditable)));
        if (replaceWasSelected) _clearSelectedState(_selectedEditable);

        replaceTarget.parentNode.replaceChild(replaceNewEl, replaceTarget);
        syncAllTextEditablePlaceholders();
        _syncEditableDisplayAttributes(replaceNewEl);
        initScrollAnimations();
        if (replaceWasSelected) {
          var nextSelected = replaceNewEl.hasAttribute('data-pb-editable')
            ? replaceNewEl
            : replaceNewEl.querySelector('[data-pb-editable][data-pb-eid="' + msg.data.eid + '"]')
              || replaceNewEl.querySelector('[data-pb-editable]');
          if (nextSelected) {
            _setSelectedEditable(nextSelected);
          } else {
            clearSelectedEditable('replace-editable-missing-selection');
          }
        }
        if (msg.data.animate === 'image-replace') {
          var replaceImageTarget = replaceNewEl.tagName === 'IMG'
            ? replaceNewEl
            : replaceNewEl.querySelector && replaceNewEl.querySelector('img');
          _finishImageReplaceCrossfade(replaceImageTarget || replaceNewEl, replaceImageGhost);
        }

        markPositionsDirty();
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('editable-replace');
        break;
      }
      case 'scroll-to-document-y': {
        // UNIT CONTRACT: y is RAW iframe document px, not the zoom-scaled
        // visual px the bridge hands parent consumers. Inbound geometry is
        // normalized on the way out of here; outbound is not, so the parent
        // divides by zoom before sending. See bridge.js _scaleNumber.
        var yData = msg.data || {};
        if (typeof yData.y === 'number' && isFinite(yData.y)) {
          window.scrollTo({
            top: Math.max(0, yData.y),
            behavior: yData.smooth === false ? 'instant' : 'smooth'
          });
        }
        break;
      }
      case 'scroll-to-section': {
        var scrollTarget = document.querySelector('[data-pb-instance="' + msg.data.instanceId + '"]');
        if (scrollTarget) {
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        break;
      }
      case 'scroll-to-eid': {
        var eidTarget = null;
        var eidData = msg.data || {};
        var targetWrapper = null;
        if (eidData.instanceId) {
          targetWrapper = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(eidData.instanceId) + '"]');
        }
        if (targetWrapper && eidData.eid) {
          eidTarget = targetWrapper.querySelector('[data-pb-eid="' + _escapeSelectorValue(eidData.eid) + '"]');
        }
        if (!eidTarget && eidData.eid) {
          // Fallback for legacy payloads that only send eid.
          eidTarget = document.querySelector('[data-pb-eid="' + _escapeSelectorValue(eidData.eid) + '"]');
        }
        if (!eidTarget && eidData.instanceId) {
          eidTarget = targetWrapper || document.querySelector('[data-pb-instance="' + _escapeSelectorValue(eidData.instanceId) + '"]');
        }
        if (eidTarget) {
          eidTarget.scrollIntoView({ behavior: 'instant', block: 'center' });
        }
        break;
      }
      case 'highlight-eid': {
        // P6: outline WITHOUT scrolling. Hovering a Publish checks row must let the
        // operator survey the whole list and see where each item lives; only a
        // click moves the canvas. A single attribute carries it so the outline
        // is CSS in the iframe (identity-and-state visuals belong here, not on
        // the parent overlay) and costs no IPC round-trip to clear.
        var hlData = msg.data || {};
        var prevHl = document.querySelectorAll('[data-pb-audit-highlight]');
        for (var hi = 0; hi < prevHl.length; hi++) {
          prevHl[hi].removeAttribute('data-pb-audit-highlight');
        }
        if (hlData.eid || hlData.instanceId) {
          var hlWrap = hlData.instanceId
            ? document.querySelector('[data-pb-instance="' + _escapeSelectorValue(hlData.instanceId) + '"]')
            : null;
          var hlTarget = null;
          if (hlWrap && hlData.eid) {
            hlTarget = hlWrap.querySelector('[data-pb-eid="' + _escapeSelectorValue(hlData.eid) + '"]');
          }
          if (!hlTarget && hlData.eid) {
            hlTarget = document.querySelector('[data-pb-eid="' + _escapeSelectorValue(hlData.eid) + '"]');
          }
          if (!hlTarget) hlTarget = hlWrap;
          if (hlTarget) hlTarget.setAttribute('data-pb-audit-highlight', '1');
        }
        break;
      }
      case 'flash-section': {
        var flashTarget = document.querySelector('[data-pb-instance="' + msg.data.instanceId + '"]');
        if (flashTarget) {
          _flashSectionWrapper(flashTarget);
        }
        break;
      }
      case 'mm-toggle-menu': {
        // MM-3 preview guidance: drive the nav's OWN hamburger handler with a
        // real click — the JS-free activation contract means the section
        // script (or exported nav.js mirror) owns the class/aria flips.
        var mmToggle =
          document.querySelector('[data-pb-shared="navigation"] .cs-toggle') ||
          document.querySelector('[data-pb-category="navigation"] .cs-toggle');
        if (mmToggle) mmToggle.click();
        break;
      }
      case 'set-ai-context': {
        // Full-set replace: the parent always sends every tagged place, so the
        // stored list is the whole truth and can be reapplied after any DOM
        // swap without the parent having to re-send.
        _aiContextTargets = Array.isArray(msg.data && msg.data.targets) ? msg.data.targets : [];
        _applyAiContext();
        break;
      }
      case 'set-ai-pick-mode': {
        _setAiPickMode(!!(msg.data && msg.data.active));
        break;
      }
      case 'set-scroll-state':
        _setScrollState(msg.data && msg.data.pinned);
        markElementRectDirty();
        break;
      case 'set-comment-mode': {
        _setCommentMode(!!(msg.data && msg.data.active));
        break;
      }
      case 'highlight-text-match': {
        // Find & Replace: locate a matched text run by (eid + beforeCtx + match
        // + afterCtx) signature inside the iframe DOM and wrap it in a
        // transient span with class pb-find-match-focus. The CSS keyframe
        // (pb-text-flash) animates the background pulse; we unwrap after
        // ~1000ms. NO state mutation — purely a visual pulse.
        var fmData = msg.data || {};
        var fmHost = null;
        if (fmData.eid) {
          if (fmData.instanceId) {
            var fmInst = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(fmData.instanceId) + '"]');
            if (fmInst) fmHost = fmInst.querySelector('[data-pb-eid="' + _escapeSelectorValue(fmData.eid) + '"]');
          }
          if (!fmHost) fmHost = document.querySelector('[data-pb-eid="' + _escapeSelectorValue(fmData.eid) + '"]');
        } else if (fmData.instanceId) {
          fmHost = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(fmData.instanceId) + '"]');
        }
        if (!fmHost) break;
        var fmFound = _findAndWrapMatch(fmHost, fmData.beforeCtx || '', fmData.match || '', fmData.afterCtx || '');
        if (!fmFound) break;
        // Auto-unwrap after the animation completes.
        setTimeout(function () { _unwrapMatchFocus(fmFound); }, 1000);
        break;
      }
      case 'format-text': {
        if (!activeEditable) break;
        if (_plainTextOnly) break;
        // Live-inject the font stylesheet BEFORE applying the class so the
        // span renders in the right face on the very first selection. The
        // payload loader (href + source) comes from
        // shared/font-catalog.buildLoaderTagsForFamily(family). Idempotent via
        // a data-pb-inline-font slug marker so repeated dropdown changes do
        // not duplicate the link.
        var fontSlug = '';
        if (msg.data && msg.data.command === 'fontFamily') {
          var fontSlugRaw = msg.data.value == null ? '' : String(msg.data.value);
          fontSlug = _sanitizeFontSlug(fontSlugRaw);
          if (fontSlugRaw && !fontSlug) break;
          if (!_isAllowedFontSlug(fontSlug)) break;
          var fontLoader = msg.data.loader || null;
          if (fontSlug) _injectFontLoader(fontLoader, fontSlug);
        }
        var sel = window.getSelection();
        var fe = window.__pbFormatEngine;
        var fmtRange = null;
        if (sel.rangeCount) {
          fmtRange = sel.getRangeAt(0);
          if (fmtRange && fmtRange.collapsed) {
            fmtRange = _restoreSelectionFromLastRange(sel) || fmtRange;
          }
        } else {
          fmtRange = _restoreSelectionFromLastRange(sel);
        }
        if (fe && fmtRange && fmtRange.collapsed && typeof fe.expandRangeToWord === 'function') {
          fmtRange = fe.expandRangeToWord(fmtRange, activeEditable) || fmtRange;
        }
        if (fe && fmtRange && !fmtRange.collapsed) {
          var formatBefore = _beginEditOperation();
          if (msg.data.command === 'bold') {
            fe.toggleFormat('strong', fmtRange);
          } else if (msg.data.command === 'italic') {
            fe.toggleFormat('em', fmtRange);
          } else if (msg.data.command === 'textColor' && msg.data.value) {
            fe.applyTextColor(msg.data.value, fmtRange);
          } else if (msg.data.command === 'fontFamily') {
            if (fe.applyFontFamily) fe.applyFontFamily(fontSlug, fmtRange);
          } else if (msg.data.command === 'link' && msg.data.value) {
            var linkHref = '';
            var linkOpenInNewTab = true;
            if (typeof msg.data.value === 'string') {
              linkHref = msg.data.value;
            } else if (msg.data.value && typeof msg.data.value === 'object') {
              linkHref = String(msg.data.value.href || '');
              if (typeof msg.data.value.openInNewTab === 'boolean') {
                linkOpenInNewTab = msg.data.value.openInNewTab;
              }
            }
            if (linkHref) {
              fe.applyLink(linkHref, fmtRange, document, { openInNewTab: linkOpenInNewTab });
            }
          } else if (msg.data.command === 'removeLink') {
            fe.removeLink(fmtRange);
          }
          if (msg.data.command === 'fontFamily') {
            _syncInlineFontRuntimeCSS();
          }
          // Update lastRange with new selection state after format
          var newSel = window.getSelection();
          if (newSel.rangeCount) {
            lastRange = newSel.getRangeAt(0).cloneRange();
          }
          _commitEditOperation(formatBefore);
          _scheduleSelectionSync();
        } else if (fe && (!fmtRange || fmtRange.collapsed)) {
          send('format-noop', {
            reason: 'no-word-or-selection',
            eid: activeEditable.getAttribute('data-pb-eid') || null,
            editSessionId: _activeEditSessionId
          });
        } else if (!fe) {
          var fallbackValue = msg.data && msg.data.value && typeof msg.data.value === 'object'
            ? (msg.data.value.href || null)
            : (msg.data.value || null);
          document.execCommand(msg.data.command, false, fallbackValue);
        }
        onContentChanged();
        break;
      }
      case 'replay-last-paste': {
        if (!activeEditable) break;
        if (_plainTextOnly) break;
        var replaySessionId = msg.data && typeof msg.data.editSessionId === 'number'
          ? msg.data.editSessionId
          : null;
        if (replaySessionId && replaySessionId !== _activeEditSessionId) break;
        var replayProfile = msg.data && typeof msg.data.profile === 'string'
          ? msg.data.profile
          : DEFAULT_PASTE_PROFILE;
        _replayLastPaste(replayProfile);
        break;
      }
      case 'apply-slash-command': {
        if (!activeEditable) break;
        if (_plainTextOnly) break;
        var slashSessionId = msg.data && typeof msg.data.editSessionId === 'number'
          ? msg.data.editSessionId
          : null;
        if (slashSessionId && slashSessionId !== _activeEditSessionId) break;
        var slashCommand = msg.data && typeof msg.data.command === 'string'
          ? msg.data.command
          : '';
        var slashBefore = _beginEditOperation();
        if (_applySlashCommand(slashCommand)) {
          _commitEditOperation(slashBefore);
          onContentChanged();
          _scheduleSelectionSync();
        }
        break;
      }
      case 'wrap-selection-with-binding': {
        if (!activeEditable) break;
        if (_plainTextOnly) break;
        var wrapSessionId = msg.data && typeof msg.data.editSessionId === 'number' ? msg.data.editSessionId : null;
        if (wrapSessionId && wrapSessionId !== _activeEditSessionId) break;
        var wrapBefore = _beginEditOperation();
        if (_applyBindingFromMessage(msg.data || {})) {
          _commitEditOperation(wrapBefore);
          onContentChanged();
          _syncBoundInfoFromSelection();
        }
        break;
      }
      case 'unwrap-binding': {
        if (!activeEditable) break;
        if (_plainTextOnly) break;
        var unwrapSessionId = msg.data && typeof msg.data.editSessionId === 'number' ? msg.data.editSessionId : null;
        if (unwrapSessionId && unwrapSessionId !== _activeEditSessionId) break;
        var unwrapBefore = _beginEditOperation();
        if (_removeBindingFromActiveEditable(msg.data || {})) {
          _commitEditOperation(unwrapBefore);
          onContentChanged();
          _syncBoundInfoFromSelection();
        }
        break;
      }
      case 'query-active-link': {
        if (!activeEditable) { send('active-link-response', null); break; }
        var qlSel = window.getSelection();
        var qlFe = window.__pbFormatEngine;
        var qlRange = null;
        if (qlSel.rangeCount) {
          qlRange = qlSel.getRangeAt(0);
          if (qlRange && qlRange.collapsed) {
            qlRange = _restoreSelectionFromLastRange(qlSel) || qlRange;
          }
        } else {
          qlRange = _restoreSelectionFromLastRange(qlSel);
        }
        if (qlFe && qlRange) {
          var linkData = qlFe.getActiveLink(qlRange);
          send('active-link-response', linkData ? {
            href: linkData.href,
            target: linkData.target || '',
            rel: linkData.rel || ''
          } : null);
        } else {
          send('active-link-response', null);
        }
        break;
      }
      case 'request-selection-snapshot': {
        if (!activeEditable) {
          send('selection-snapshot', {
            eid: null,
            editSessionId: _activeEditSessionId,
            html: '',
            text: '',
            hasSelection: false
          });
          break;
        }
        var snapshotSessionId = msg.data && typeof msg.data.editSessionId === 'number'
          ? msg.data.editSessionId
          : null;
        if (snapshotSessionId && snapshotSessionId !== _activeEditSessionId) break;

        var snapshotRange = _getSelectionRangeWithinActiveEditable();
        var snapshotPayload = snapshotRange ? _buildSelectionSnapshotPayload(snapshotRange) : null;
        send('selection-snapshot', {
          eid: activeEditable.getAttribute('data-pb-eid') || null,
          editSessionId: _activeEditSessionId,
          html: snapshotPayload ? snapshotPayload.html : '',
          text: snapshotPayload ? snapshotPayload.text : '',
          rangeSnapshot: snapshotRange && snapshotPayload
            ? captureRangeSnapshot(activeEditable, snapshotRange)
            : null,
          hasSelection: !!snapshotPayload
        });
        break;
      }
      case 'replace-selection-html': {
        if (!activeEditable) break;
        var replaceSessionId = msg.data && typeof msg.data.editSessionId === 'number'
          ? msg.data.editSessionId
          : null;
        if (replaceSessionId && replaceSessionId !== _activeEditSessionId) break;

        var replacementHtml = msg.data && typeof msg.data.html === 'string'
          ? msg.data.html
          : '';
        var sanitizedReplacement = sanitizeHTML(
          replacementHtml,
          PASTE_PROFILES.MATCH_SITE_STYLE,
          activeEditable
        );
        var replacementBefore = _beginEditOperation();
        var requestedRangeSnapshot = msg.data && msg.data.rangeSnapshot
          ? msg.data.rangeSnapshot
          : null;
        var replacedSnapshot = _insertHtmlAtSelection(sanitizedReplacement, requestedRangeSnapshot);
        if (!replacedSnapshot && lastRange) {
          _setSelectionToRange(lastRange);
          replacedSnapshot = _insertHtmlAtSelection(sanitizedReplacement, null);
        }
        if (!replacedSnapshot) {
          send('selection-replaced', {
            eid: activeEditable.getAttribute('data-pb-eid') || null,
            editSessionId: _activeEditSessionId,
            ok: false
          });
          break;
        }
        _commitEditOperation(replacementBefore);
        onContentChanged();
        _scheduleSelectionSync();
        send('selection-replaced', {
          eid: activeEditable.getAttribute('data-pb-eid') || null,
          editSessionId: _activeEditSessionId,
          ok: true
        });
        break;
      }
      case 'update-theme-var': {
        document.documentElement.style.setProperty(msg.data.name, msg.data.value);
        markPositionsDirty();
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('theme');
        break;
      }
      case 'update-font': {
        var fontName = msg.data.font;
        document.body.style.fontFamily = "'" + fontName + "', 'Roboto', Arial, sans-serif";
        var fontLoader = msg.data && Object.prototype.hasOwnProperty.call(msg.data, 'loader')
          ? msg.data.loader
          : undefined;
        _setGlobalFontLoader('body', fontLoader, _fontMarkerFromName(fontName));
        markPositionsDirty();
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('font');
        break;
      }
      case 'update-heading-font': {
        var hFont = msg.data.font;
        var hWeight = msg.data.weight || '700';
        var hFontStyle = document.getElementById('pb-heading-font');
        if (!hFontStyle) {
          hFontStyle = document.createElement('style');
          hFontStyle.id = 'pb-heading-font';
          document.head.appendChild(hFontStyle);
        }
        if (hFont) {
          hFontStyle.textContent = "h1,h2,h3,h4,h5,h6,.cs-title,.cs-h3,.cs-topper { font-family: '" + hFont + "', 'Roboto', Arial, sans-serif; font-weight: " + hWeight + "; }";
          var hFontLoader = msg.data && Object.prototype.hasOwnProperty.call(msg.data, 'loader')
            ? msg.data.loader
            : undefined;
          _setGlobalFontLoader('heading', hFontLoader, _fontMarkerFromName(hFont));
        } else {
          hFontStyle.textContent = '';
          _setGlobalFontLoader('heading', null, '');
        }
        markPositionsDirty();
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('heading-font');
        break;
      }
      case 'update-overrides': {
        var overridesStyle = document.getElementById('pb-overrides');
        if (overridesStyle) overridesStyle.textContent = msg.data.css || '';
        // CSS overrides can move/resize elements without any DOM mutation.
        // Force geometry/hover refresh so glass UI tracks immediately.
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('overrides');
        break;
      }
      case 'sync-section-text-html': {
        var syncInstanceId = msg.data && msg.data.instanceId;
        var syncHtml = msg.data && typeof msg.data.html === 'string' ? msg.data.html : null;
        var syncLabel = msg.data && msg.data.label ? msg.data.label : null;
        if (!syncInstanceId || syncHtml === null) break;

        var syncWrapper = document.querySelector('[data-pb-instance="' + syncInstanceId + '"]');
        if (!syncWrapper) break;

        var syncDoc = new DOMParser().parseFromString('<div>' + syncHtml + '</div>', 'text/html');
        var syncRoot = syncDoc.body.firstElementChild;
        if (!syncRoot) break;

        // Phase 20: wrap the per-node mutation loop in ONE view transition so
        // shared-element morphing animates all changed elements together.
        // Gated by label inside _runWithViewTransition — non-reorder syncs
        // fall through to direct mutation.
        _runWithViewTransition(syncWrapper, syncLabel, function() {
          var liveTextNodes = syncWrapper.querySelectorAll('[data-pb-editable="text"][data-pb-eid]');
          for (var sti = 0; sti < liveTextNodes.length; sti++) {
            var liveNode = liveTextNodes[sti];
            var liveEid = liveNode.getAttribute('data-pb-eid');
            if (!liveEid) continue;
            var srcNode = syncRoot.querySelector('[data-pb-eid="' + liveEid + '"]');
            if (!srcNode) continue;
            // The node the user is actively editing is the source of truth; this
            // echo exists only to propagate a shared-section edit to OTHER
            // instances. Overwriting the live node mid-type causes churn/caret
            // jitter (and, before the plain-Enter fix, made lines vanish), so skip it.
            if (activeEditable && activeEditable === liveNode) continue;
            liveNode.innerHTML = srcNode.innerHTML;
            syncTextEditablePlaceholder(liveNode);
            _updateEditingPill(liveNode);
          }
          _syncEditableDisplayAttributes(syncWrapper);
        });

        if (activeEditable && !document.contains(activeEditable)) {
          _restoreSuspendedDragTargets();
          activeEditable = null;
          activeEditableInitialHTML = null;
          activeEditableInitialTagName = null;
          _intentionallyRemovedProtectedPaths = [];
          lastRange = null;
          _sendSelectionCleared();
        }

        if (_selectedEditable && !document.contains(_selectedEditable)) {
          clearSelectedEditable('text-html-sync-detached');
        }

        // Replaced innerHTML drops the tag attributes; the intent survives.
        _applyAiContext();
        _syncInlineFontRuntimeCSS();
        markPositionsDirty();
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('section-text-html');
        break;
      }
      case 'sync-section-html': {
        var syncFullInstanceId = msg.data && msg.data.instanceId;
        var syncFullHtml = msg.data && typeof msg.data.html === 'string' ? msg.data.html : null;
        var syncFullLabel = msg.data && msg.data.label ? msg.data.label : null;
        if (!syncFullInstanceId || syncFullHtml === null) break;

        var syncFullWrapper = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(syncFullInstanceId) + '"]');
        if (!syncFullWrapper) break;

        var selectedEidBeforeSync = _selectedEditable && syncFullWrapper.contains(_selectedEditable)
          ? _selectedEditable.getAttribute('data-pb-eid')
          : null;
        if (selectedEidBeforeSync) _clearSelectedState(_selectedEditable);

        if (activeEditable && syncFullWrapper.contains(activeEditable)) {
          finishEditing();
        }

        // Phase 20: wrap full-section innerHTML replacement in a view transition
        // when the label is a reorder label. Marker re-injection and scroll-anim
        // re-init run AFTER the mutation completes inside the same callback so
        // the browser snapshots the new state correctly.
        _runWithViewTransition(syncFullWrapper, syncFullLabel, function() {
          syncFullWrapper.innerHTML = syncFullHtml;
          syncAllTextEditablePlaceholders();
          _syncEditableDisplayAttributes(syncFullWrapper);
          _injectHeaderZoneMarkers();
          initScrollAnimations();
        });

        if (selectedEidBeforeSync) {
          var selectedAfterSync = syncFullWrapper.querySelector('[data-pb-eid="' + _escapeSelectorValue(selectedEidBeforeSync) + '"]');
          if (selectedAfterSync) {
            _setSelectedEditable(selectedAfterSync);
          } else {
            clearSelectedEditable('section-html-sync-missing-selection');
          }
        } else if (_selectedEditable && !document.contains(_selectedEditable)) {
          clearSelectedEditable('section-html-sync-detached');
        }

        // Replaced innerHTML drops the tag attributes; the intent survives.
        _applyAiContext();
        _syncInlineFontRuntimeCSS();
        markPositionsDirty();
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('section-html');
        break;
      }
      case 'update-button-styles': {
        var btnStyle = document.getElementById('pb-button-overrides');
        if (btnStyle) btnStyle.textContent = msg.data.css || '';
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('button-styles');
        break;
      }
      case 'update-form-styles': {
        var formStyle = document.getElementById('pb-form-overrides');
        if (formStyle) formStyle.textContent = msg.data.css || '';
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('form-styles');
        break;
      }
      case 'update-button-effect-js': {
        // Clean up previous effect listeners/state
        var effBtns = document.querySelectorAll('main :is(.cs-button-solid, .cs-button-transparent):not(.cs-btn-custom)');
        for (var eb = 0; eb < effBtns.length; eb++) {
          if (effBtns[eb]._pbEffectCleanup) { effBtns[eb]._pbEffectCleanup(); effBtns[eb]._pbEffectCleanup = null; }
          effBtns[eb].style.transform = '';
          effBtns[eb].classList.remove('pb-animate');
        }
        var existingEffectScript = document.getElementById('pb-button-effect-js');
        if (existingEffectScript) existingEffectScript.remove();
        var effectJS = msg.data.js || '';
        if (effectJS) {
          var effectScript = document.createElement('script');
          effectScript.id = 'pb-button-effect-js';
          effectScript.textContent = effectJS;
          document.body.appendChild(effectScript);
        }
        break;
      }
      case 'update-svg-attr': {
        var svgAttrTarget = findEditableByPath(msg.data);
        if (svgAttrTarget && (svgAttrTarget.tagName === 'svg' || svgAttrTarget.tagName === 'SVG')) {
          if (msg.data.value) {
            svgAttrTarget.setAttribute(msg.data.attr, msg.data.value);
          } else {
            svgAttrTarget.removeAttribute(msg.data.attr);
          }
        }
        markElementRectDirty();
        markHoverTagDirty();
        markStyleSnapshotDirty('svg-attribute');
        break;
      }
      case 'query-element-rect': {
        reportElementRect();
        break;
      }
      case 'reselect-editable': {
        // Reselection after a parent-side mutation that replaced the previously
        // selected DOM node (e.g. the heading-type retag in glass-panel-text).
        // Find the new element by eid first, fall back to editableIndex within
        // the section wrapper, then update _selectedEditable + observer and
        // emit a fresh element-rect so floating panels can repoint.
        var reselectData = msg.data || {};
        var reselectInstanceId = reselectData.instanceId || '';
        var reselectEid = reselectData.eid || '';
      var reselectIndex = (typeof reselectData.editableIndex === 'number') ? reselectData.editableIndex : -1;
      if (!reselectInstanceId) break;
      var reselectWrapper = document.querySelector('[data-pb-instance="' + reselectInstanceId + '"]');
      if (!reselectWrapper) break;
      var nextSelected = null;
        if (reselectEid) {
          nextSelected = reselectWrapper.querySelector('[data-pb-eid="' + reselectEid + '"]');
        }
        if (!nextSelected && reselectIndex >= 0) {
          var reselectEditables = reselectWrapper.querySelectorAll('[data-pb-editable]');
          nextSelected = reselectEditables[reselectIndex] || null;
      }
      if (!nextSelected) break;
      _setSelectedEditable(nextSelected);
      _syncEditableDisplayAttributes(nextSelected);
      reportElementRect();
      break;
    }
      case 'query-style-snapshot': {
        // Re-find the element by instanceId+eid (never cache across renders) and
        // report its own computed style + iframe-resolved token identity. Mirrors
        // the reselect-editable re-find; uses _escapeSelectorValue consistently.
        // Eid-less form (instanceId only): snapshot the section's first element
        // child — the section-panel background readback target.
        var ssData = msg.data || {};
        var ssInstanceId = ssData.instanceId || '';
        var ssEid = ssData.eid || '';
        var ssEl = null;
        if (ssInstanceId) {
          var ssWrapper = document.querySelector('[data-pb-instance="' + _escapeSelectorValue(ssInstanceId) + '"]');
          if (ssWrapper) {
            if (ssEid) {
              ssEl = ssWrapper.querySelector('[data-pb-eid="' + _escapeSelectorValue(ssEid) + '"]');
            } else {
              var ssChild = ssWrapper.firstElementChild;
              while (ssChild && ssChild.hasAttribute('data-pb-ignore')) ssChild = ssChild.nextElementSibling;
              ssEl = ssChild;
            }
          }
        }
        if (!ssEl && ssEid) ssEl = document.querySelector('[data-pb-eid="' + _escapeSelectorValue(ssEid) + '"]');
        if (!ssEl) break;
        if (ssData.reason === 'override-push') {
          // Verify read: suppress transitions (data-pb-verifying rule in the
          // editing CSS) and force a reflow so a mid-transition color/background
          // can't read as "the override didn't win". Removed synchronously.
          ssEl.setAttribute('data-pb-verifying', '');
          void ssEl.offsetWidth;
          try {
            send('style-snapshot', buildStyleSnapshot(ssEl, ssData));
          } finally {
            ssEl.removeAttribute('data-pb-verifying');
          }
          break;
        }
        send('style-snapshot', buildStyleSnapshot(ssEl, ssData));
        break;
      }
      case 'query-spacing-group': {
        var groupData = msg.data || {};
        var groupEid = groupData.eid || '';
        var groupScope = groupData.scope || 'self';
        var elementOverrides = groupData.elementOverrides || {};
        var groupEls = getSpacingGroupForEid(groupEid, groupScope);
        var groupItems = groupEls.map(function(groupEl) {
          var itemEid = groupEl.getAttribute('data-pb-eid') || '';
          return {
            eid: itemEid,
            rect: getSpacingRectForEditable(groupEl),
            computed: getSpacingComputedForEditable(groupEl),
            template: getSpacingTemplateForEditable(groupEl),
            override: elementOverrides[itemEid] || {}
          };
        });
        send('spacing-group-result', {
          eid: groupEid,
          scope: groupScope,
          requestId: groupData.requestId || null,
          zoom: getCurrentZoom(),
          items: groupItems
        });
        break;
      }
      case 'query-rects-for-eids': {
        // Align/distribute measurement (UX Pack F4b). Coordinates are RAW
        // iframe CSS px on purpose — margins are written in CSS px, so no
        // zoom normalization is applied (zoom rides along for any consumer
        // that wants visual coords). Contiguity and shared-parent are
        // verified HERE against the real rendered tree, never inferred.
        var rqData = msg.data || {};
        var rqIds = Array.isArray(rqData.eids) ? rqData.eids : [];
        var rqWrapper = rqData.instanceId
          ? document.querySelector('[data-pb-instance="' + _escapeSelectorValue(rqData.instanceId) + '"]')
          : null;
        var rqEls = [];
        var rqMissing = [];
        for (var rq = 0; rq < rqIds.length; rq++) {
          var rqEl = rqWrapper
            ? rqWrapper.querySelector('[data-pb-eid="' + _escapeSelectorValue(rqIds[rq]) + '"]')
            : null;
          if (rqEl) rqEls.push(rqEl);
          else rqMissing.push(rqIds[rq]);
        }
        rqEls.sort(function (a, b) {
          return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        });
        var rqParent = rqEls.length ? rqEls[0].parentElement : null;
        var rqSameParent = true;
        for (var rp = 1; rp < rqEls.length; rp++) {
          if (rqEls[rp].parentElement !== rqParent) {
            rqSameParent = false;
            break;
          }
        }
        var rqContiguous = rqSameParent && rqEls.length > 0;
        if (rqContiguous) {
          for (var rc = 1; rc < rqEls.length; rc++) {
            if (rqEls[rc - 1].nextElementSibling !== rqEls[rc]) {
              rqContiguous = false;
              break;
            }
          }
        }
        var rqParentStyle = rqParent ? getComputedStyle(rqParent) : null;
        var rqItems = rqEls.map(function (el) {
          var elStyle = getComputedStyle(el);
          var elTag = el.tagName.toLowerCase();
          return {
            eid: el.getAttribute('data-pb-eid') || '',
            rect: getSpacingRectForEditable(el),
            tagName: elTag,
            replaced:
              elTag === 'img' ||
              elTag === 'svg' ||
              elTag === 'picture' ||
              elTag === 'video' ||
              elTag === 'iframe' ||
              elTag === 'canvas',
            computed: {
              display: elStyle.display || '',
              position: elStyle.position || '',
              transform: elStyle.transform || 'none',
              visibility: elStyle.visibility || '',
              marginTop: elStyle.marginTop || '',
              marginBottom: elStyle.marginBottom || ''
            }
          };
        });
        send('rects-for-eids-result', {
          instanceId: rqData.instanceId || '',
          requestId: rqData.requestId || null,
          zoom: getCurrentZoom(),
          contiguous: rqContiguous,
          sameParent: rqSameParent,
          missing: rqMissing,
          parent: rqParentStyle
            ? {
                display: rqParentStyle.display || '',
                gap: rqParentStyle.gap || '',
                writingMode: rqParentStyle.writingMode || 'horizontal-tb'
              }
            : null,
          items: rqItems
        });
        break;
      }
      case 'set-transient-spacing-css': {
        var transientPayload = msg.data || {};
        var transientRules = Array.isArray(transientPayload.rules) ? transientPayload.rules : [];
        var styleEl = document.getElementById('pb-transient-spacing');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'pb-transient-spacing';
          document.head.appendChild(styleEl);
        }
        var cssParts = [];
        for (var ti = 0; ti < transientRules.length; ti++) {
          var rule = transientRules[ti] || {};
          if (!rule.eid || !rule.declarations) continue;
          var safe = _escapeSelectorValue(rule.eid);
          var selector = rule.targetPicture
            ? 'picture:has(> [data-pb-eid="' + safe + '"][data-pb-eid="' + safe + '"])'
            : '[data-pb-eid="' + safe + '"][data-pb-eid="' + safe + '"]';
          cssParts.push(selector + ' { ' + rule.declarations + ' !important; }');
        }
        styleEl.textContent = cssParts.join('\n');
        break;
      }
      case 'clear-transient-spacing-css': {
        var clearStyleEl = document.getElementById('pb-transient-spacing');
        if (clearStyleEl) clearStyleEl.textContent = '';
        break;
      }
      case 'set-spacing-dim': {
        var dimPayload = msg.data || {};
        var dimEids = Array.isArray(dimPayload.eids) ? dimPayload.eids : [];
        var dimStyle = document.getElementById('pb-spacing-dim');
        if (!dimStyle) {
          dimStyle = document.createElement('style');
          dimStyle.id = 'pb-spacing-dim';
          document.head.appendChild(dimStyle);
        }
        var dimSelectors = dimEids.map(function(eid) {
          var safe = _escapeSelectorValue(eid);
          return '[data-pb-eid="' + safe + '"][data-pb-eid="' + safe + '"]';
        });
        var brightRule = dimSelectors.length
          ? '\n[data-pb-section] :is(' + dimSelectors.join(',') + ') { opacity: 1 !important; }'
          : '';
        dimStyle.textContent = '[data-pb-section] [data-pb-editable] { transition: opacity 180ms ease; opacity: 0.38; }' + brightRule;
        document.body.setAttribute('data-pb-spacing-dim', '');
        break;
      }
      case 'clear-spacing-dim': {
        var clearDim = document.getElementById('pb-spacing-dim');
        if (clearDim) clearDim.textContent = '';
        document.body.removeAttribute('data-pb-spacing-dim');
        break;
      }
      case 'select-by-eid': {
        var selData = msg.data || {};
        var selEid = selData.eid || '';
        if (!selEid) break;
        // Tier 4 audit fix: eids are only unique per section (stampEditableIds
        // numbers e1,e2,… per root), so scope the lookup to the sender's
        // instance first; global lookup is a legacy-payload fallback only.
        var selWrapper = selData.instanceId
          ? document.querySelector('[data-pb-instance="' + _escapeSelectorValue(selData.instanceId) + '"]')
          : null;
        var selTarget = selWrapper
          ? selWrapper.querySelector('[data-pb-eid="' + _escapeSelectorValue(selEid) + '"]')
          : null;
        if (!selTarget) {
          selTarget = document.querySelector('[data-pb-eid="' + _escapeSelectorValue(selEid) + '"]');
        }
        if (!selTarget) break;
        // Synthesize a click on the target element to reuse the entire selection
        // pipeline (panel, badges, overlay, undo, etc.).
        var clickEvt;
        try {
          clickEvt = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
        } catch (e) {
          clickEvt = document.createEvent('MouseEvents');
          clickEvt.initEvent('click', true, true);
        }
        selTarget.dispatchEvent(clickEvt);
        break;
      }
      case 'select-ancestor': {
        var ancestorData = msg.data || {};
        // Tier 4 Phase 2: base resolves from the sent eid first, then the
        // persistent selection (_selectedEditable), then the text-edit-session
        // var (activeEditable). The old activeEditable-only gate meant
        // escalation silently did nothing outside a text edit.
        var ancestorBase = null;
        if (ancestorData.eid) {
          // Instance-scoped first (eids repeat across sections), global only
          // as a legacy-payload fallback — same policy as select-by-eid.
          var ancestorWrapper = ancestorData.instanceId
            ? document.querySelector('[data-pb-instance="' + _escapeSelectorValue(ancestorData.instanceId) + '"]')
            : null;
          ancestorBase = ancestorWrapper
            ? ancestorWrapper.querySelector('[data-pb-eid="' + _escapeSelectorValue(ancestorData.eid) + '"]')
            : null;
          if (!ancestorBase) {
            ancestorBase = document.querySelector('[data-pb-eid="' + _escapeSelectorValue(ancestorData.eid) + '"]');
          }
        }
        ancestorBase = ancestorBase || _selectedEditable || activeEditable;
        // domHops is a RAW parentElement step count (safe to replay verbatim);
        // ancestorDepth is an index into the filtered trail and is only used
        // as a legacy fallback when domHops is absent.
        var hopsRaw = ancestorData.domHops != null ? ancestorData.domHops : ancestorData.ancestorDepth;
        var depth = Number(hopsRaw);
        if (!ancestorBase || !Number.isFinite(depth) || depth < 0) break;
        var ancestor = ancestorBase;
        for (var ai = 0; ai < depth && ancestor; ai++) {
          ancestor = ancestor.parentElement;
        }
        selectSpacingRelativeTarget(ancestor);
        break;
      }
      case 'select-relative-editable': {
        var relData = msg.data || {};
        var relBase = _selectedEditable || activeEditable;
        if (!relBase) break;
        if (relData.direction === 'child') {
          selectSpacingRelativeTarget(relBase.querySelector('[data-pb-editable][data-pb-eid]'));
        } else if (relData.direction === 'parent') {
          var parentCandidate = relBase.parentElement;
          while (parentCandidate && !parentCandidate.hasAttribute('data-pb-section')) {
            if (parentCandidate.hasAttribute && parentCandidate.hasAttribute('data-pb-editable') && parentCandidate.hasAttribute('data-pb-eid')) {
              break;
            }
            parentCandidate = parentCandidate.parentElement;
          }
          selectSpacingRelativeTarget(parentCandidate);
        }
        break;
      }
      case 'set-zoom': {
        var zoomLevel = msg.data && msg.data.zoom;
        if (typeof zoomLevel === 'number' && zoomLevel > 0) {
          document.documentElement.style.zoom = zoomLevel;
        } else {
          document.documentElement.style.zoom = '';
        }
        // Report new positions since zoom changes layout
        markPositionsDirty();
        break;
      }
      case 'restore-scroll': {
        var y = msg.data && msg.data.scrollY;
        var capturedZoom = parseFloat(msg.data && msg.data.capturedZoom);
        var currentZoom = parseFloat(msg.data && msg.data.currentZoom);
        var safeCapturedZoom = isFinite(capturedZoom) && capturedZoom > 0 ? capturedZoom : 1;
        var safeCurrentZoom = isFinite(currentZoom) && currentZoom > 0 ? currentZoom : getCurrentZoom();
        if (typeof y === 'number' && y > 0) {
          var scaledY = y * (safeCapturedZoom / safeCurrentZoom);
          window.scrollTo(0, scaledY);
        }
        requestAnimationFrame(function() {
          send('scroll-restored', { scrollY: window.scrollY });
        });
        break;
      }
      case 'set-preview-mode': {
        var modeData = msg.data || {};
        // "interactionDisabled" is the new name; "enabled" is the wire-compatible
        // old one, and they mean the same thing. Read both so a parent on either
        // side of the EA-3 change drives the same behaviour.
        _previewMode = !!(
          modeData.interactionDisabled === undefined
            ? modeData.enabled
            : modeData.interactionDisabled
        );
        if (_previewMode && activeEditable) finishEditing();
        // Toggle editing CSS so sticky nav is restored in preview
        var editingStyle = document.getElementById('pb-editing');
        if (editingStyle) editingStyle.disabled = _previewMode;

        // EA-3 — entrances arm for the REAL Preview toggle ONLY.
        //
        // Deliberately NOT keyed on "_previewMode": that is true for a read-only
        // tab as well (no write access, an agent review, a history preview),
        // and a read-only tab is still the editing canvas. The locked decision
        // is that every editing surface stays static.
        if (modeData.visualPreview === true) {
          setEntranceSuppressed(false);
          // The parent owns project state and passes the boolean; the iframe
          // never reasons about state.entrance itself.
          setTemplateEntrancesEnabled(modeData.templateEntrances !== false);
          // A REAL preview enter, and only here. Replaying from an incidental
          // re-init would restart the whole page's stagger on every edit.
          replayBuilderEntrances();
        } else if (modeData.visualPreview === false) {
          setEntranceSuppressed(true);
        }
        break;
      }
      case 'preview-entrance': {
        // EA-10 (contracts §5): the selector is built HERE, from validated
        // ids, so the wire never carries a selector and the scope is exact —
        // page (null,null), one section, or one element. The whole-builder
        // replay exists elsewhere (replayBuilderEntrances); this is narrower
        // by contract.
        var pvInstance = typeof msg.instanceId === 'string' ? msg.instanceId : '';
        var pvEid = typeof msg.eid === 'string' ? msg.eid : '';
        var ID_OK = /^[\w-]+$/;
        if (pvInstance && !ID_OK.test(pvInstance)) break;
        if (pvEid && !ID_OK.test(pvEid)) break;
        if (pvEid && !pvInstance) break; // an eid is meaningless without its section
        var pvSelector = '';
        if (pvInstance) {
          pvSelector = '[data-pb-instance="' + pvInstance + '"]';
          if (pvEid) pvSelector += ' [data-pb-eid="' + pvEid + '"]';
        }
        if (pvSelector) {
          var pvScope = null;
          try { pvScope = document.querySelector(pvSelector); } catch (err) { pvScope = null; }
          if (!pvScope) break;
          try { pvScope.scrollIntoView({ block: 'center', behavior: 'instant' }); } catch (err) {
            try { pvScope.scrollIntoView(); } catch (err2) {}
          }
        }
        previewEntranceOnce(pvSelector);
        break;
      }
      case 'query-section-spacing': {
        requestAnimationFrame(function() {
        var spacingMap = {};
        var allWrappers = document.querySelectorAll('[data-pb-instance]');
        for (var si = 0; si < allWrappers.length; si++) {
          var wrap = allWrappers[si];
          var iid = wrap.getAttribute('data-pb-instance');
          var wrapStyles = window.getComputedStyle(wrap);
          var child = wrap.querySelector('[data-pb-section]') || wrap.firstElementChild;
          var childStyles = child ? window.getComputedStyle(child) : null;
          spacingMap[iid] = {
            marginTop: wrapStyles.marginTop,
            marginBottom: wrapStyles.marginBottom,
            paddingTop: childStyles ? childStyles.paddingTop : null,
            paddingBottom: childStyles ? childStyles.paddingBottom : null
          };
        }
        var spacingRootPx = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
        var spacingPresets = {};
        var spacingProbe = document.createElement('div');
        spacingProbe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;width:0;height:0;';
        document.body.appendChild(spacingProbe);
        var spacingPresetValues = Array.isArray(msg.data.presetValues) ? msg.data.presetValues : [];
        spacingPresetValues.slice(0, 20).forEach(function(value) {
          spacingProbe.style.paddingTop = '';
          spacingProbe.style.paddingTop = value;
          if (spacingProbe.style.paddingTop) spacingPresets[value] = window.getComputedStyle(spacingProbe).paddingTop;
        });
        spacingProbe.remove();
        send('section-spacing-response', {
          requestId: msg.data.requestId,
          generation: msg.data.generation,
          stateEpoch: msg.data.stateEpoch,
          viewport: { name: msg.data.viewportName, width: window.innerWidth },
          rootPx: spacingRootPx,
          values: spacingMap,
          presets: spacingPresets
        });
        });
        break;
      }
      case 'apply-icon-tints': {
        _applyIconTints();
        break;
      }
      case 'remove-icon-tints': {
        _removeIconTints();
        break;
      }
      case 'retint-icon': {
        var wrapper = document.querySelector('[data-pb-instance="' + msg.data.instanceId + '"]');
        if (!wrapper) break;
        var editables = wrapper.querySelectorAll('[data-pb-editable="image"]');
        var target = editables[msg.data.editableIndex];
        if (!target || target.tagName !== 'IMG') break;
        // Remove existing tint if present
        if (target.hasAttribute('data-tinted')) {
          var origSrc = target.getAttribute('data-orig-src');
          if (origSrc) target.setAttribute('src', origSrc);
          target.removeAttribute('data-tinted');
          target.removeAttribute('data-orig-src');
          var s = target.getAttribute('style') || '';
          var c = s.split(';').filter(function(p) {
            var k = (p.split(':')[0] || '').trim().toLowerCase();
            return k && k.indexOf('mask-') === -1 && k !== 'background-color' && k !== '--pb-icon-tint';
          }).join(';').replace(/^;+|;+$/g, '');
          if (c) target.setAttribute('style', c);
          else target.removeAttribute('style');
        }
        // Set tint color and reapply
        if (msg.data.tintColor) {
          target.setAttribute('data-tint-color', msg.data.tintColor);
        } else {
          target.removeAttribute('data-tint-color');
        }
        _applyIconTints();
        break;
      }
    }
  });

  function findEditableByPath(data) {
    if (!data || !data.instanceId) return null;
    var wrapper = document.querySelector('[data-pb-instance="' + data.instanceId + '"]');
    if (!wrapper) return null;
    if (typeof data.eid === 'string' && data.eid) {
      var byEid = wrapper.querySelector('[data-pb-eid="' + data.eid + '"]');
      if (byEid) return byEid;
    }
    if (typeof data.editableIndex !== 'number') return null;
    var editables = wrapper.querySelectorAll('[data-pb-editable]');
    return editables[data.editableIndex] || null;
  }

  function _limitText(value, maxLen) {
    var str = String(value || '');
    if (str.length <= maxLen) return str;
    return str.slice(0, maxLen);
  }

  function _sanitizeSource(value) {
    if (!value) return '';
    var str = String(value);
    var httpAt = str.indexOf('http://', 1);
    var httpsAt = str.indexOf('https://', 1);
    var duplicateAt = -1;
    if (httpAt !== -1 && httpsAt !== -1) duplicateAt = Math.min(httpAt, httpsAt);
    else duplicateAt = Math.max(httpAt, httpsAt);
    if (duplicateAt > 0) str = str.slice(duplicateAt);
    try {
      var base = (window.location && window.location.origin && window.location.origin !== 'null')
        ? window.location.origin
        : window.location.href;
      var u = new URL(str, base);
      return _limitText(u.origin + u.pathname, 300);
    } catch (err) {
      return _limitText(str, 300);
    }
  }

  function _toErrorPayload(kind, errLike) {
    var message = '';
    var name = '';
    var source = '';
    var line = 0;
    var col = 0;
    var stack = '';
    var href = _sanitizeSource(window.location && window.location.href ? window.location.href : '');

    if (kind === 'error') {
      var e = errLike || {};
      if (e.error && e.error.name) name = _limitText(String(e.error.name), 120);
      else if (e.name) name = _limitText(String(e.name), 120);
      message = _limitText(e.message || 'Unknown iframe error', 240);
      source = _sanitizeSource(e.filename || '');
      line = Number.isFinite(e.lineno) ? e.lineno : 0;
      col = Number.isFinite(e.colno) ? e.colno : 0;
      if (e.error && e.error.stack) {
        stack = _limitText(String(e.error.stack), 1200);
      }
    } else {
      var reason = errLike && errLike.reason;
      if (reason && typeof reason === 'object') {
        if (reason.name) name = _limitText(String(reason.name), 120);
        message = _limitText(reason.message || String(reason), 240);
        if (reason.stack) stack = _limitText(String(reason.stack), 1200);
      } else {
        message = _limitText(reason || 'Unhandled promise rejection in preview', 240);
      }
    }

    return {
      type: kind,
      name: name || '',
      message: message || 'Unknown preview error',
      source: source,
      line: line,
      col: col,
      stack: stack || '',
      href: href || '',
      nonce: __PB_NONCE__
    };
  }

  // Surface iframe runtime errors to parent so preview failures aren't silent.
  window.addEventListener('error', function(e) {
    var payload = _toErrorPayload('error', e);
    send('iframe-error', payload);
  });

  window.addEventListener('unhandledrejection', function(e) {
    var payload = _toErrorPayload('unhandledrejection', e);
    send('iframe-error', payload);
  });

  // Report scroll position to parent so it can restore after re-render.
  // The parent can't read scrollY directly due to sandbox cross-origin.
  var _scrollRafId = null;
  window.addEventListener('scroll', function() {
    if (_scrollRafId) return;
    _scrollRafId = requestAnimationFrame(function() {
      _scrollRafId = null;
      send('scroll-position', { scrollY: window.scrollY, zoom: getCurrentZoom() });
    });
  }, { passive: true });
`;
}
